import React from 'react';
import { FormGroup, FormInput, AdvancedColorInput, NavButtons } from '../common';

const CongressAppLinksPreview = ({ settings, globalSettings }) => {
  const stores = [
    {
      key: 'appleUrl',
      imageKey: 'congressAppApple',
      fallbackLabel: 'App Store',
    },
    {
      key: 'googleUrl',
      imageKey: 'congressAppGoogle',
      fallbackLabel: 'Google Play',
    },
  ].filter(store => settings.congressAppLinks?.[store.key]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-5">
      <div
        className="rounded-2xl p-5 shadow-sm"
        style={{
          backgroundColor: settings.congressAppBackgroundColor || '#f8f9fa',
          color: settings.bodyTextColor || '#1f2937',
        }}
      >
        <p className="text-base font-semibold text-slate-900">
          {settings.congressAppHeadline || 'Get the congress app'}
        </p>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
          {stores.length > 0 ? (
            stores.map(store => {
              const imageUrl = globalSettings?.imageUrls?.[store.imageKey] || '';
              return imageUrl ? (
                <img
                  key={store.key}
                  src={imageUrl}
                  alt={store.fallbackLabel}
                  className="h-auto max-w-[180px]"
                />
              ) : (
                <div
                  key={store.key}
                  className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
                >
                  {store.fallbackLabel}
                </div>
              );
            })
          ) : (
            <p className="text-sm text-slate-500">
              Add at least one store URL to preview the app links block.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const CongressAppLinksSection = ({
  settings,
  onUpdate,
  onNavigate,
  regmailEnabled,
  globalSettings,
}) => {
  const handleCongressAppLinkChange = (platform, value) => {
    const nextLinks = {
      ...(settings.congressAppLinks || {}),
      [platform]: value,
    };
    onUpdate('congressAppLinks', nextLinks);
  };

  const resolveNextSectionKey = () => {
    if (settings.includeCalendarButton) return 'calendarButton';
    if (settings.includeWalletButtons) return 'wallet';
    if (regmailEnabled) return 'regmailCustomization';
    return 'images';
  };

  return (
    <>
      <FormGroup title="Congress App Links">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormInput
              label="Headline"
              value={settings.congressAppHeadline || ''}
              onChange={e => onUpdate('congressAppHeadline', e.target.value)}
              placeholder="Get the congress app"
            />
            <div className="flex items-end">
              <p className="text-xs text-gray-500">
                Placement is controlled in Text Templates via <code>[[CONGRESS_APP_LINKS]]</code>.
              </p>
            </div>
          </div>
          <AdvancedColorInput
            label="Box Background Color"
            value={settings.congressAppBackgroundColor || '#f8f9fa'}
            onChange={value => onUpdate('congressAppBackgroundColor', value)}
          />
          <p className="text-xs text-gray-500">
            Add the store URLs below. The badge images are managed in the admin
            global settings.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormInput
              label="Apple App Store URL"
              value={settings.congressAppLinks?.appleUrl || ''}
              onChange={e => handleCongressAppLinkChange('appleUrl', e.target.value)}
              onPaste={true}
              placeholder="https://apps.apple.com/..."
            />
            <FormInput
              label="Google Play Store URL"
              value={settings.congressAppLinks?.googleUrl || ''}
              onChange={e => handleCongressAppLinkChange('googleUrl', e.target.value)}
              onPaste={true}
              placeholder="https://play.google.com/store/apps/details?id=..."
            />
          </div>
        </div>
      </FormGroup>

      <FormGroup title="Preview">
        <CongressAppLinksPreview
          settings={settings}
          globalSettings={globalSettings}
        />
      </FormGroup>

      <NavButtons
        onPrev={() => onNavigate('content')}
        onNext={() => onNavigate(resolveNextSectionKey())}
      />
    </>
  );
};

export default CongressAppLinksSection;
